/**
 * Tab 1 — Configuración: a guided, one-thing-at-a-time wizard that reuses the
 * Registro journey's visual language (WizardStep clone of QuestionScreen, ChoiceCard,
 * FieldHelp) but lives INSIDE the console shell. Six steps: tipo → punto de partida →
 * ponderación → umbrales → avanzado (saltable) → revisar y guardar.
 */
import { useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_RISK_MATRIX,
  RECOMMENDED_RISK_MATRIX,
  RISK_FACTOR_REFERENCE,
  RISK_SUBFACTOR_CATALOGS,
  catalogForMode,
  sectorRiskMatrix,
  type RiskCatalogEntry,
  type RiskFactorKey,
  type RiskNivel,
} from '@cumpli/shared';
import { ChevronDown, Lock, RotateCcw, Sparkles, SquarePen, X } from 'lucide-react';
import { openAssistant } from '../../components/assistant/page-actions';
import { Button, Card, Input, Label, Pill, ProgressBar, cn } from '../../components/ui';
import { ChoiceCard } from '../../components/flow';
import { formatDate } from '../../lib/hooks';
import {
  ENTITY_MODES,
  ENTITY_MODE_COPY,
  EDITOR_PAGE,
  FACTORS,
  InfoBubble,
  LevelPicker,
  NIVELES,
  SUB_FAMILIES,
  ThresholdDial,
  USER_OFF_NOTE,
  UsarToggle,
  WeightRow,
  WizardStep,
  explainOf,
  familyAppliesInMode,
  subOf,
  subfactorAppliesInMode,
  sum,
  type BandOverrides,
  type MatrixController,
  type SubFamilyKey,
} from './shared';

const STEPS = ['tipo', 'partida', 'ponderacion', 'umbrales', 'avanzado', 'revision'] as const;
type StepKey = (typeof STEPS)[number];

/**
 * Configuración — a two-state machine. A saved matrix (version > 0) lands on a
 * clean read-only summary; version 0 (first run) or "Editar matriz" opens the
 * guided wizard. Finishing the wizard (save) flips back to the summary.
 */
export default function Configuracion({ ctrl }: { ctrl: MatrixController }) {
  if (ctrl.version > 0 && !ctrl.editing) return <SavedView ctrl={ctrl} />;
  return <Wizard ctrl={ctrl} />;
}

function Wizard({ ctrl }: { ctrl: MatrixController }) {
  const [idx, setIdx] = useState(0);
  const key: StepKey = STEPS[idx]!;
  const stepLabel = `Paso ${idx + 1} de ${STEPS.length}`;
  const progress = idx / (STEPS.length - 1);
  const next = () => setIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setIdx((i) => Math.max(i - 1, 0));

  const backButton =
    idx > 0 ? (
      <button
        type="button"
        onClick={back}
        className="w-full rounded-2xl py-3 text-sm font-bold text-ink-soft hover:bg-panel"
      >
        Atrás
      </button>
    ) : undefined;

  return (
    <div className="mx-auto flex min-h-[62vh] w-full max-w-2xl flex-col">
      {/* When editing an already-saved matrix, offer a way back to the summary
          without saving. Version 0 (first run) has nothing to cancel to. */}
      {ctrl.version > 0 && (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={ctrl.cancelEdit}
            className="inline-flex items-center gap-1 text-xs font-semibold text-ink-soft hover:text-ink"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} /> Cancelar edición
          </button>
        </div>
      )}
      <div className="mb-6">
        <ProgressBar value={progress} />
      </div>

      {key === 'tipo' && <StepTipo ctrl={ctrl} stepLabel={stepLabel} onNext={next} />}
      {key === 'partida' && (
        <StepPartida ctrl={ctrl} stepLabel={stepLabel} onNext={next} back={backButton} />
      )}
      {key === 'ponderacion' && (
        <StepPonderacion ctrl={ctrl} stepLabel={stepLabel} onNext={next} back={backButton} />
      )}
      {key === 'umbrales' && (
        <StepUmbrales ctrl={ctrl} stepLabel={stepLabel} onNext={next} back={backButton} />
      )}
      {key === 'avanzado' && (
        <StepAvanzado ctrl={ctrl} stepLabel={stepLabel} onNext={next} back={backButton} />
      )}
      {key === 'revision' && <StepRevision ctrl={ctrl} stepLabel={stepLabel} back={backButton} />}
    </div>
  );
}

// ---------------------------------------------------------- Paso 1: tipo ----

function StepTipo({
  ctrl,
  stepLabel,
  onNext,
}: {
  ctrl: MatrixController;
  stepLabel: string;
  onNext: () => void;
}) {
  return (
    <WizardStep
      step={stepLabel}
      question="¿Qué tipo de entidad regulada eres?"
      helper="Define qué factores e indicadores de riesgo AML/CFT aplican a tu matriz."
      primaryLabel="Continuar"
      onPrimary={onNext}
    >
      <div className="grid gap-3">
        {ENTITY_MODES.map((m) => (
          <ChoiceCard
            key={m.key}
            icon={m.icon}
            label={m.label}
            selected={ctrl.mode === m.key}
            onClick={() => {
              if (ctrl.canSwitchMode && m.key !== ctrl.mode) ctrl.switchMode(m.key);
            }}
          />
        ))}
      </div>
      {!ctrl.canSwitchMode ? (
        <p className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-line bg-canvas px-3 py-2 text-xs font-semibold text-ink-faint">
          <Lock className="h-3.5 w-3.5" strokeWidth={2} /> Solo lectura: el tipo lo cambia el gerente de cumplimiento.
        </p>
      ) : (
        <p className="mt-4 text-xs text-ink-faint">{ENTITY_MODE_COPY.help}</p>
      )}
    </WizardStep>
  );
}

// ------------------------------------------------------- Paso 2: partida ----

function StepPartida({
  ctrl,
  stepLabel,
  onNext,
  back,
}: {
  ctrl: MatrixController;
  stepLabel: string;
  onNext: () => void;
  back: ReactNode;
}) {
  const grupos = useMemo(
    () => Array.from(new Set(ctrl.modePresets.map((p) => p.grupo))),
    [ctrl.modePresets]
  );
  return (
    <WizardStep
      step={stepLabel}
      question="Elige un punto de partida"
      helper="Una calibración base que luego puedes ajustar. Recomendado está fundamentado; cada subcategoría trae el sesgo típico de su sector."
      primaryLabel="Continuar"
      onPrimary={onNext}
      secondary={back}
    >
      <div className="grid gap-3">
        <ChoiceCard
          label="Recomendado (fundamentado)"
          sub="35 · 25 · 20 · 20 (Wolfsberg + Acuerdo SBP 1-2026)"
          selected={ctrl.activePreset === 'recomendado'}
          onClick={() => ctrl.canEdit && ctrl.applyPreset(RECOMMENDED_RISK_MATRIX)}
        />
        <ChoiceCard
          label="Preliminar (reunión)"
          sub="50 · 10 · 10 · 30 (vector inicial, sin fundamento externo)"
          selected={ctrl.activePreset === 'preliminar'}
          onClick={() => ctrl.canEdit && ctrl.applyPreset(DEFAULT_RISK_MATRIX)}
        />

        {grupos.map((g) => (
          <div key={g} className="mt-2">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">{g}</div>
            <div className="grid gap-3">
              {ctrl.modePresets
                .filter((p) => p.grupo === g)
                .map((p) => (
                  <ChoiceCard
                    key={p.key}
                    label={p.label}
                    sub={`${p.supervisor} · ${p.weights.cliente}·${p.weights.jurisdiccion}·${p.weights.producto}·${p.weights.canal}`}
                    selected={ctrl.activeSector?.key === p.key}
                    onClick={() => ctrl.canEdit && ctrl.applyPreset(sectorRiskMatrix(p))}
                  />
                ))}
            </div>
          </div>
        ))}
      </div>
      {ctrl.activeSector && (
        <p className="mt-4 text-xs text-ink-faint">
          <span className="font-semibold text-ink-soft">{ctrl.activeSector.label}</span>:{' '}
          {ctrl.activeSector.fundamento}
        </p>
      )}
    </WizardStep>
  );
}

// --------------------------------------------------- Paso 3: ponderación ----

function StepPonderacion({
  ctrl,
  stepLabel,
  onNext,
  back,
}: {
  ctrl: MatrixController;
  stepLabel: string;
  onNext: () => void;
  back: ReactNode;
}) {
  // Multi-abierto pero cerrado por defecto: el detalle fino se pide, no se impone.
  const [openFamilies, setOpenFamilies] = useState<Set<SubFamilyKey>>(() => new Set());
  const { mode, weights, subweights, userDisabled, canEdit, total, sumOk, subSumsOk } = ctrl;

  return (
    <WizardStep
      step={stepLabel}
      question="Pondera los factores"
      helper="Reparte 100% entre los cuatro factores. Abre una familia para ajustar sus sub-factores. Toca la ⓘ para ver de dónde sale cada peso."
      primaryLabel="Continuar"
      onPrimary={onNext}
      primaryDisabled={!sumOk || !subSumsOk}
      secondary={back}
    >
      <Card data-tour="risk-matrix-weights">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-lg">Ponderación de factores</h3>
          <Pill tone={sumOk ? 'green' : 'red'}>Suma {total}%</Pill>
        </div>
        <div className="space-y-3">
          {FACTORS.map((f) => {
            const hasSub = (SUB_FAMILIES as string[]).includes(f.key);
            const subFam = f.key as SubFamilyKey;
            const open = hasSub && openFamilies.has(subFam);
            const subTotal = hasSub ? sum(subweights[subFam]) : 100;
            const ref = RISK_FACTOR_REFERENCE.find((r) => r.key === f.key);
            const familyModeOff = !familyAppliesInMode(mode, f.key as RiskFactorKey);
            const familyUserOff = userDisabled.families.includes(f.key as RiskFactorKey);
            const familyOff = familyModeOff || familyUserOff;
            const anySubModeOff =
              hasSub && (ref?.subfactors ?? []).some((sf) => !subfactorAppliesInMode(mode, subFam, sf.key));
            const anySubUserOff =
              hasSub && (ref?.subfactors ?? []).some((sf) => userDisabled.subfactors.includes(`${subFam}.${sf.key}`));
            return (
              <div key={f.key} className={cn('rounded-xl border border-line p-3', familyOff && 'opacity-60')}>
                <WeightRow
                  label={f.label}
                  aka={f.aka}
                  hint={f.hint}
                  help={explainOf(ref)}
                  value={weights[f.key]}
                  disabled={!canEdit || familyOff}
                  onChange={(v) => ctrl.setWeight(f.key, v)}
                  badge={familyModeOff ? 'Solo financiero' : familyUserOff ? 'No usado' : undefined}
                />
                {familyModeOff ? (
                  <p className="mt-2 rounded-lg border border-line bg-canvas px-2.5 py-1.5 text-xs text-ink-faint">
                    {ENTITY_MODE_COPY.transactionalNote}
                  </p>
                ) : familyUserOff ? (
                  <p className="mt-2 rounded-lg border border-line bg-canvas px-2.5 py-1.5 text-xs text-ink-faint">
                    {USER_OFF_NOTE}
                  </p>
                ) : null}
                {hasSub && !familyOff && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenFamilies((prev) => {
                          const next = new Set(prev);
                          if (next.has(subFam)) next.delete(subFam);
                          else next.add(subFam);
                          return next;
                        })
                      }
                      className="flex w-full items-center justify-between text-xs font-semibold text-ink-soft hover:text-ink"
                    >
                      <span className="flex items-center gap-1.5">
                        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} strokeWidth={2} />
                        Sub-factores
                      </span>
                      <Pill tone={subTotal === 100 ? 'green' : 'red'}>{subTotal}%</Pill>
                    </button>
                    {open && (
                      <div className="mt-3 space-y-3 border-t border-line pt-3">
                        {(ref?.subfactors ?? []).map((sf) => {
                          const subModeOff = !subfactorAppliesInMode(mode, subFam, sf.key);
                          const subUserOff = userDisabled.subfactors.includes(`${subFam}.${sf.key}`);
                          return (
                            <WeightRow
                              key={sf.key}
                              label={sf.label}
                              hint={sf.fundamento}
                              help={explainOf(sf)}
                              value={subweights[subFam][sf.key as keyof (typeof subweights)[SubFamilyKey]]}
                              disabled={!canEdit || subModeOff || subUserOff}
                              small
                              badge={subModeOff ? 'Solo financiero' : subUserOff ? 'No usado' : undefined}
                              onChange={(v) => ctrl.setSub(subFam, sf.key, v)}
                            />
                          );
                        })}
                        {anySubModeOff && (
                          <p className="rounded-lg border border-line bg-canvas px-2.5 py-1.5 text-xs text-ink-faint">
                            {ENTITY_MODE_COPY.transactionalNote}
                          </p>
                        )}
                        {anySubUserOff && (
                          <p className="rounded-lg border border-line bg-canvas px-2.5 py-1.5 text-xs text-ink-faint">
                            {USER_OFF_NOTE}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!sumOk && <p className="mt-3 text-sm font-semibold text-tone-red">Las ponderaciones deben sumar 100%.</p>}
        {!subSumsOk && (
          <p className="mt-2 text-sm font-semibold text-tone-red">Los sub-factores de cada familia deben sumar 100%.</p>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={() => ctrl.applyPreset(RECOMMENDED_RISK_MATRIX)}
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-soft hover:text-ink"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} /> Restaurar valores recomendados
          </button>
        )}
      </Card>
    </WizardStep>
  );
}

// ------------------------------------------------------ Paso 4: umbrales ----

function StepUmbrales({
  ctrl,
  stepLabel,
  onNext,
  back,
}: {
  ctrl: MatrixController;
  stepLabel: string;
  onNext: () => void;
  back: ReactNode;
}) {
  const { thresholds, canEdit, thresholdsOk } = ctrl;
  return (
    <WizardStep
      step={stepLabel}
      question="Define los umbrales de nivel"
      helper="El puntaje compuesto (0–100) se clasifica en bajo, medio o alto según estos cortes."
      primaryLabel="Continuar"
      onPrimary={onNext}
      primaryDisabled={!thresholdsOk}
      secondary={back}
    >
      <Card>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="threshold-medio">Medio desde</Label>
            <Input
              id="threshold-medio"
              type="number"
              min={1}
              max={98}
              value={thresholds.medio}
              disabled={!canEdit}
              onChange={(e) => ctrl.setThresholds((t) => ({ ...t, medio: Number(e.target.value) || 0 }))}
            />
          </div>
          <div>
            <Label htmlFor="threshold-alto">Alto desde</Label>
            <Input
              id="threshold-alto"
              type="number"
              min={2}
              max={100}
              value={thresholds.alto}
              disabled={!canEdit}
              onChange={(e) => ctrl.setThresholds((t) => ({ ...t, alto: Number(e.target.value) || 0 }))}
            />
          </div>
        </div>
        <div className="mt-5">
          <ThresholdDial thresholds={thresholds} />
        </div>
        {!thresholdsOk && (
          <p className="mt-3 text-sm font-semibold text-tone-red">El umbral alto debe ser mayor que el medio.</p>
        )}
      </Card>
    </WizardStep>
  );
}

// ------------------------------------------------------ Paso 5: avanzado ----

function StepAvanzado({
  ctrl,
  stepLabel,
  onNext,
  back,
}: {
  ctrl: MatrixController;
  stepLabel: string;
  onNext: () => void;
  back: ReactNode;
}) {
  return (
    <WizardStep
      step={stepLabel}
      question="Ajustes avanzados (opcional)"
      helper="Puedes saltar este paso. Aquí re-calificas la banda de un indicador (p. ej. Panamá en alto) o eliges cuáles usar. Todo parte del catálogo recomendado y alimenta el simulador."
      primaryLabel="Continuar"
      onPrimary={onNext}
      secondary={back}
    >
      <div className="mb-4 flex items-start gap-2 rounded-xl border border-primary/25 bg-primary/5 p-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
        <p className="text-xs leading-relaxed text-ink-soft">
          ¿Prefieres que lo haga Cumpli AI?{' '}
          <button
            type="button"
            onClick={openAssistant}
            className="font-semibold text-primary-dark underline underline-offset-2 hover:text-primary"
          >
            Pídeselo al Agente
          </button>{' '}
          y descríbele el cambio: él ajusta las bandas por ti.
        </p>
      </div>
      <IndicatorEditor ctrl={ctrl} />
    </WizardStep>
  );
}

/**
 * Editor manual de indicadores: re-califica la banda (LevelPicker) y elige Usar / No
 * usar por indicador y por sub-factor. Parte del catálogo base recomendado; persiste
 * en localStorage (por tipo) y alimenta el simulador. La edición por lenguaje natural
 * ahora vive en el Agente — aquí solo la edición manual.
 */
function IndicatorEditor({ ctrl }: { ctrl: MatrixController }) {
  const { mode, canEdit, bandOverrides, disabledForMode } = ctrl;
  const [openCat, setOpenCat] = useState<string | null>(null);
  const [queries, setQueries] = useState<Record<string, string>>({});
  const [limits, setLimits] = useState<Record<string, number>>({});

  const cats: { catKey: string; famLabel: string; subLabel: string }[] = [];
  for (const fam of RISK_FACTOR_REFERENCE) {
    if (!familyAppliesInMode(mode, fam.key)) continue;
    if (fam.subfactors.length === 0) {
      if (RISK_SUBFACTOR_CATALOGS['producto']) cats.push({ catKey: 'producto', famLabel: fam.label, subLabel: fam.label });
      continue;
    }
    for (const sf of fam.subfactors) {
      if (!subfactorAppliesInMode(mode, fam.key, sf.key)) continue;
      const catKey = `${fam.key}.${sf.key}`;
      if (RISK_SUBFACTOR_CATALOGS[catKey]) cats.push({ catKey, famLabel: fam.label, subLabel: sf.label });
    }
  }

  const totalEdits = Object.values(bandOverrides).reduce((n, m) => n + Object.keys(m ?? {}).length, 0);
  const totalDisabled = Object.values(disabledForMode).reduce((n, arr) => n + (arr?.length ?? 0), 0);

  function setBand(catKey: string, entry: RiskCatalogEntry, band: RiskNivel) {
    const nextOv: BandOverrides = { ...bandOverrides, [catKey]: { ...(bandOverrides[catKey] ?? {}) } };
    if (band === entry.band) {
      delete nextOv[catKey]![entry.key];
      if (Object.keys(nextOv[catKey]!).length === 0) delete nextOv[catKey];
    } else {
      nextOv[catKey]![entry.key] = band;
    }
    ctrl.setBandOverrides(nextOv);
  }
  function setBandBulk(catKey: string, entries: readonly RiskCatalogEntry[], band: RiskNivel) {
    const nextOv: BandOverrides = { ...bandOverrides, [catKey]: { ...(bandOverrides[catKey] ?? {}) } };
    for (const e of entries) {
      if (band === e.band) delete nextOv[catKey]![e.key];
      else nextOv[catKey]![e.key] = band;
    }
    if (Object.keys(nextOv[catKey]!).length === 0) delete nextOv[catKey];
    ctrl.setBandOverrides(nextOv);
  }
  function setEntryEnabled(catKey: string, entryKey: string, enabled: boolean) {
    const set = new Set(disabledForMode[catKey] ?? []);
    if (enabled) set.delete(entryKey);
    else set.add(entryKey);
    const next = { ...disabledForMode };
    if (set.size) next[catKey] = [...set];
    else delete next[catKey];
    ctrl.setDisabledForMode(next);
  }
  function setSubfactorEnabled(catKey: string, allEntries: readonly RiskCatalogEntry[], enabled: boolean) {
    const next = { ...disabledForMode };
    if (enabled) delete next[catKey];
    else next[catKey] = allEntries.map((e) => e.key);
    ctrl.setDisabledForMode(next);
  }
  function clearCat(catKey: string) {
    const nextOv = { ...bandOverrides };
    delete nextOv[catKey];
    ctrl.setBandOverrides(nextOv);
    const nd = { ...disabledForMode };
    delete nd[catKey];
    ctrl.setDisabledForMode(nd);
  }

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-serif text-lg">Editor de indicadores</h3>
        {totalEdits + totalDisabled > 0 && (
          <button
            type="button"
            onClick={() => {
              ctrl.setBandOverrides({});
              ctrl.setDisabledForMode({});
            }}
            className="inline-flex items-center gap-1 text-xs font-semibold text-ink-soft hover:text-ink"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} /> Restaurar todo ({totalEdits + totalDisabled})
          </button>
        )}
      </div>

      <div className="space-y-2">
        {cats.map(({ catKey, famLabel, subLabel }) => {
          const base = catalogForMode(RISK_SUBFACTOR_CATALOGS[catKey] ?? [], mode);
          const ov = bandOverrides[catKey] ?? {};
          const editedCount = Object.keys(ov).length;
          const disOff = new Set(disabledForMode[catKey] ?? []);
          const disabledCount = disOff.size;
          const subOff = base.length > 0 && base.every((e) => disOff.has(e.key));
          const open = openCat === catKey;
          const q = (queries[catKey] ?? '').trim().toLowerCase();
          const big = base.length > 12;
          const limit = limits[catKey] ?? EDITOR_PAGE;
          const shown = q
            ? base.filter((e) => e.label.toLowerCase().includes(q) || e.key.toLowerCase().includes(q))
            : base.slice(0, limit);
          const remaining = q ? 0 : base.length - shown.length;
          return (
            <div key={catKey} className="rounded-xl border border-line">
              <button
                type="button"
                onClick={() => setOpenCat(open ? null : catKey)}
                className="flex w-full items-center justify-between px-3 py-2 text-left"
              >
                <span className="min-w-0">
                  <span className="text-sm font-semibold text-ink">{subLabel}</span>
                  <span className="ml-2 text-xs text-ink-faint">{famLabel} · {base.length} indicadores</span>
                </span>
                <span className="flex items-center gap-2">
                  {editedCount > 0 && <Pill tone="amber">{editedCount} editado(s)</Pill>}
                  {disabledCount > 0 && (
                    <span className="rounded-full bg-line px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                      {subOff ? 'sin usar' : `${disabledCount} sin usar`}
                    </span>
                  )}
                  <ChevronDown className={cn('h-4 w-4 text-ink-soft transition-transform', open && 'rotate-180')} strokeWidth={2} />
                </span>
              </button>
              {open && (
                <div className="border-t border-line px-3 py-3">
                  {big && (
                    <input
                      value={queries[catKey] ?? ''}
                      onChange={(e) => setQueries((s) => ({ ...s, [catKey]: e.target.value }))}
                      placeholder={`Buscar entre ${base.length} indicadores…`}
                      className="mb-3 w-full rounded-lg border border-line-strong bg-canvas px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  )}
                  <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-line bg-canvas px-2.5 py-2">
                    <span className="min-w-0 text-xs font-semibold text-ink-soft">
                      {subOff ? 'Sub-factor desactivado — no cuenta en el puntaje' : 'Usar este sub-factor en el puntaje'}
                    </span>
                    <UsarToggle
                      on={!subOff}
                      disabled={!canEdit}
                      onToggle={() => setSubfactorEnabled(catKey, base, subOff)}
                      label="sub-factor"
                    />
                  </div>
                  <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-canvas px-2.5 py-2">
                    <span className="text-xs font-semibold text-ink-soft">Poner {shown.length} mostrado(s) en:</span>
                    {NIVELES.map((n) => (
                      <button
                        key={n}
                        type="button"
                        disabled={!canEdit || shown.length === 0}
                        onClick={() => setBandBulk(catKey, shown, n)}
                        className="rounded-md border border-line px-2.5 py-0.5 text-xs font-semibold capitalize text-ink-soft transition-colors hover:border-primary hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    {shown.map((e) => {
                      const eff = ov[e.key] ?? e.band;
                      const edited = ov[e.key] && ov[e.key] !== e.band;
                      const entryOff = disOff.has(e.key);
                      return (
                        <div key={e.key} className={cn('rounded-lg px-1 py-1.5', entryOff && 'opacity-60')}>
                          <div className="flex items-start gap-1.5">
                            <span className={cn('min-w-0 flex-1 text-sm leading-snug text-ink', entryOff && 'line-through')}>
                              {e.label}
                            </span>
                            {entryOff ? (
                              <span className="mt-0.5 shrink-0 text-[10px] font-semibold uppercase text-ink-faint">sin usar</span>
                            ) : (
                              edited && <span className="mt-0.5 shrink-0 text-[10px] font-semibold uppercase text-tone-amber">editado</span>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            {!entryOff && <LevelPicker value={eff} onChange={(band) => canEdit && setBand(catKey, e, band)} />}
                            <UsarToggle
                              on={!entryOff}
                              disabled={!canEdit}
                              onToggle={() => setEntryEnabled(catKey, e.key, entryOff)}
                              label="indicador"
                            />
                            <InfoBubble text={e.fundamento} label={`Ver fundamento legal — ${e.label}`} />
                          </div>
                        </div>
                      );
                    })}
                    {shown.length === 0 && <p className="px-1 py-2 text-xs text-ink-faint">Sin coincidencias.</p>}
                    {remaining > 0 && (
                      <div className="flex items-center gap-3 px-1 pt-1">
                        <button
                          type="button"
                          onClick={() => setLimits((s) => ({ ...s, [catKey]: shown.length + 20 }))}
                          className="inline-flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs font-semibold text-ink-soft hover:border-primary hover:text-ink"
                        >
                          <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} /> Cargar más ({remaining})
                        </button>
                        {big && (
                          <button
                            type="button"
                            onClick={() => setLimits((s) => ({ ...s, [catKey]: base.length }))}
                            className="text-[11px] font-semibold text-ink-faint hover:text-ink"
                          >
                            Mostrar todos ({base.length})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {(editedCount > 0 || disabledCount > 0) && (
                    <button
                      type="button"
                      onClick={() => clearCat(catKey)}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-ink-soft hover:text-ink"
                    >
                      <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} /> Restaurar este sub-factor
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-[10px] text-ink-faint">
        Se guarda con la matriz al Guardar — versionada por entidad.
      </p>
    </Card>
  );
}

// ------------------------------------------------------ Paso 6: revisión ----

function StepRevision({
  ctrl,
  stepLabel,
  back,
}: {
  ctrl: MatrixController;
  stepLabel: string;
  back: ReactNode;
}) {
  const { weights, thresholds, activePreset, activeSector, canEdit, canSave, dirty, saving } = ctrl;
  const modeLabel = ENTITY_MODES.find((m) => m.key === ctrl.mode)?.label ?? ctrl.mode;
  const partida =
    activePreset === 'recomendado'
      ? 'Recomendado (fundamentado)'
      : activePreset === 'preliminar'
        ? 'Preliminar (reunión)'
        : activeSector
          ? activeSector.label
          : 'Personalizado';
  const totalEdits = Object.values(ctrl.bandOverrides).reduce((n, m) => n + Object.keys(m ?? {}).length, 0);
  const totalDisabled = Object.values(ctrl.disabledForMode).reduce((n, arr) => n + (arr?.length ?? 0), 0);

  return (
    <WizardStep
      step={stepLabel}
      question="Revisa y guarda"
      helper={canEdit ? 'Si todo está bien, guarda la matriz. El motor recalcula el riesgo de cada cliente al guardar.' : 'No tienes permiso para guardar la matriz.'}
      primaryLabel={saving ? 'Guardando…' : 'Guardar matriz'}
      onPrimary={ctrl.save}
      primaryDisabled={!canEdit || !canSave || !dirty || saving}
      secondary={back}
    >
      <dl className="divide-y divide-line rounded-2xl border border-line bg-panel px-5 shadow-card">
        <Row label="Tipo de entidad" value={modeLabel} />
        <Row label="Punto de partida" value={partida} />
        <Row
          label="Ponderación"
          value={`${weights.cliente} · ${weights.jurisdiccion} · ${weights.producto} · ${weights.canal}`}
        />
        <Row label="Umbrales" value={`medio ${thresholds.medio} · alto ${thresholds.alto}`} />
        <Row
          label="Ajustes avanzados"
          value={totalEdits + totalDisabled > 0 ? `${totalEdits + totalDisabled} cambio(s)` : 'Ninguno'}
        />
      </dl>
      {!dirty && canEdit && (
        <p className="mt-4 text-sm text-ink-faint">No hay cambios sin guardar respecto a la matriz vigente.</p>
      )}
      {!canSave && (
        <p className="mt-4 text-sm font-semibold text-tone-red">
          Revisa las ponderaciones y umbrales — deben sumar 100% y el umbral alto ser mayor que el medio.
        </p>
      )}
    </WizardStep>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5">
      <dt className="text-sm text-ink-faint">{label}</dt>
      <dd className="text-right font-semibold text-ink">{value}</dd>
    </div>
  );
}

// ------------------------------------------------------- Vista guardada ----


/**
 * Read-only summary of the ACTIVE saved matrix (version > 0). Reads straight from
 * `ctrl.savedMatrix`, so it always reflects what's persisted — not any in-memory
 * draft. Carries the `risk-matrix-weights` demo-tour anchor (the wizard's copy is
 * hidden in this state).
 */
function SavedView({ ctrl }: { ctrl: MatrixController }) {
  // Desglose de sub-factores por familia: visible a un clic, cerrado por defecto.
  const [openSubs, setOpenSubs] = useState<Set<string>>(() => new Set());
  const m = ctrl.savedMatrix;
  if (!m) return null; // version > 0 guarantees a matrix; keeps TS honest

  const modeEntry = ENTITY_MODES.find((e) => e.key === ctrl.mode);
  const ModeIcon = modeEntry?.icon;
  const sub = subOf(m);

  const tpl = m.template;
  const bandas = tpl?.bandOverrides
    ? Object.values(tpl.bandOverrides).reduce((n, o) => n + Object.keys(o).length, 0)
    : 0;
  const disForMode = tpl?.disabledIndicators?.[ctrl.mode];
  const sinUsar = disForMode ? Object.values(disForMode).reduce((n, arr) => n + arr.length, 0) : 0;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5">
      {/* Encabezado: versión + fecha + editar */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-medium text-ink">Matriz vigente</h2>
          <p className="mt-1 text-sm text-ink-soft">
            Versión {m.version} · Actualizada {formatDate(m.updatedAt, '—')}
          </p>
        </div>
        {ctrl.canEdit ? (
          <Button onClick={ctrl.startEdit}>
            <SquarePen className="h-4 w-4" strokeWidth={2} /> Editar matriz
          </Button>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-xl border border-line bg-canvas px-3 py-2 text-xs font-semibold text-ink-faint">
            <Lock className="h-3.5 w-3.5" strokeWidth={2} /> Solo lectura: la edita el gerente de cumplimiento.
          </span>
        )}
      </div>

      {/* Tipo de entidad */}
      <Card className="flex items-center gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary-dark">
          {ModeIcon ? <ModeIcon className="h-5 w-5" strokeWidth={2} /> : null}
        </span>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-faint">Tipo de entidad</div>
          <div className="font-serif text-lg text-ink">{modeEntry?.label ?? ctrl.mode}</div>
        </div>
      </Card>

      {/* Ponderación — lleva el ancla del tour (la del wizard queda oculta aquí) */}
      <Card data-tour="risk-matrix-weights">
        <h3 className="mb-4 font-serif text-lg">Ponderación de factores</h3>
        <div className="space-y-3.5">
          {FACTORS.map((f) => {
            const value = m.weights[f.key];
            const hasSub = (SUB_FAMILIES as string[]).includes(f.key);
            const ref = RISK_FACTOR_REFERENCE.find((r) => r.key === f.key);
            const famSub = hasSub ? (sub[f.key as SubFamilyKey] as Record<string, number>) : null;
            return (
              <div key={f.key}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-ink">
                    {f.label}
                    {f.aka ? <span className="ml-1 text-xs font-normal text-ink-faint">· {f.aka}</span> : null}
                  </span>
                  <span className="font-mono text-sm font-bold tabular-nums text-ink">{value}%</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${value}%` }} />
                </div>
                {famSub && ref && (
                  <SubBreakdown
                    open={openSubs.has(f.key)}
                    onToggle={() =>
                      setOpenSubs((prev) => {
                        const next = new Set(prev);
                        if (next.has(f.key)) next.delete(f.key);
                        else next.add(f.key);
                        return next;
                      })
                    }
                    subfactors={ref.subfactors.map((sf) => ({
                      label: sf.label,
                      value: famSub[sf.key] ?? 0,
                    }))}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Umbrales + estadísticas de la plantilla */}
      <div className="grid gap-5 sm:grid-cols-2">
        <Card>
          <h3 className="mb-4 font-serif text-lg">Umbrales</h3>
          <ThresholdDial thresholds={m.thresholds} />
        </Card>
        <Card>
          <h3 className="mb-4 font-serif text-lg">Plantilla de indicadores</h3>
          <dl className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-sm text-ink-soft">Bandas re-calificadas</dt>
              <dd className="font-mono text-lg font-bold tabular-nums text-ink">{bandas}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-sm text-ink-soft">Indicadores sin usar</dt>
              <dd className="font-mono text-lg font-bold tabular-nums text-ink">{sinUsar}</dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-ink-faint">
            Ajustes sobre el catálogo base recomendado, versionados con la matriz.
          </p>
        </Card>
      </div>

      {/* Editar con IA: un solo botón; las sugerencias aparecen al pedirlas */}
      <AiEditCard />
    </div>
  );
}

/**
 * "Editar con IA": un solo botón que abre el asistente (⌘K). Las operaciones
 * disponibles de esta página viven DENTRO del drawer como funciones (consulta /
 * acción propone→confirma) — nada de paredes de prompts en la página.
 */
function AiEditCard() {
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-serif text-lg">
            <Sparkles className="h-4 w-4 text-primary" strokeWidth={2} /> Editar con IA
          </h3>
          <p className="mt-0.5 text-sm text-ink-soft">
            El Agente consulta o ajusta tu matriz; propone y tú confirmas.
          </p>
        </div>
        <button
          type="button"
          onClick={openAssistant}
          className="rounded-xl border border-primary bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-dark"
        >
          Editar con IA
        </button>
      </div>
    </Card>
  );
}

/**
 * Desglose de sub-factores en la vista guardada: colapsado por defecto (cero
 * ruido), al abrir muestra cada sub-peso con una mini-barra — suficiente para
 * verificar de un vistazo un ajuste hecho por el Agente sin entrar a editar.
 */
function SubBreakdown({
  open,
  onToggle,
  subfactors,
}: {
  open: boolean;
  onToggle: () => void;
  subfactors: { label: string; value: number }[];
}) {
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-1 text-xs font-semibold text-ink-faint transition-colors hover:text-ink-soft"
      >
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} strokeWidth={2} />
        Sub-factores
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 pl-4">
          {subfactors.map((sf) => (
            <div key={sf.label}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-ink-soft">{sf.label}</span>
                <span className="font-mono text-xs font-semibold tabular-nums text-ink">{sf.value}%</span>
              </div>
              <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-line">
                <div className="h-full rounded-full bg-primary/55" style={{ width: `${sf.value}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
